import { Box, Typography, TextField, Button, Link, Alert } from '@mui/material'
import { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useAppSelector } from '@store/hooks'
import { useForgotPasswordMutation } from '@store/api/authApi'

const ForgotPassword: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
  })
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const navigate = useNavigate()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    try {
      await forgotPassword(formData).unwrap()
      setSuccess(true)
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to send reset email. Please try again.'
      setError(errorMessage)
    }
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Check Your Email
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" paragraph>
          If an account with that email exists, a password reset link has been sent to your email address.
        </Typography>

        <Alert severity="success" sx={{ mb: 2 }}>
          Please check your email and follow the instructions to reset your password.
        </Alert>
        
        <Box textAlign="center">
          <Link component={RouterLink} to="/auth/login">
            Back to Login
          </Link>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Forgot Password
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" paragraph>
        Enter your email address and we&apos;ll send you a link to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
          required
          disabled={isLoading}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{ mt: 3, mb: 2 }}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </Button>
        
        <Box textAlign="center">
          <Link component={RouterLink} to="/auth/login">
            Back to Login
          </Link>
        </Box>
      </Box>
    </Box>
  )
}

export default ForgotPassword
